import React, { FC, useEffect, useRef, useState } from 'react';

import { NativeResources } from '@ariksa/abac/api';
import { V1CloudEventsInsightCategory } from '@ariksa/audittrail/api';
import { CloudProvider } from '@ariksa/inventory-core/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { each, isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import {
  ActionButton,
  ChartMixedIcon,
  EllipsisText,
  formatBytes,
  formatTooltip,
  PermissionsActionsIcon,
  PlusCountTag,
  renderRiskContext,
  renderTableHeaderWithLoader,
  renderTooltipFieldValue,
  StackedCell,
  SuddenActivityIcon,
  Tag,
  WithResourceIcon,
} from 'components/index';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { selectIncidentResponse } from 'containers/Visibility/IncidentResponse/selectors';
import { actions } from 'containers/Visibility/IncidentResponse/slice';
import { limitedString } from 'utils/string';

import { renderCloudEventsInsights, renderRequestCount } from '../../utils';
import { CloudEventsActivityDrawer } from '../Activity/CloudEventsActivityDrawer';
import { HourlyAccessSummary } from '../Summary/HourlyAccessSummary';

import { flattenSelectedPaths } from './utils';

interface Props {
  groupId: string;
  groupName: string;
  date: string;
  insightCategories: V1CloudEventsInsightCategory[];
  resourceType: NativeResources;
}

export const ActivityGroupResourcesTable: FC<Props> = props => {
  const { groupId, groupName, date, insightCategories, resourceType } = props;
  const { getCloudNativeName } = useResourceType();
  const dispatch = useDispatch();
  const {
    resources,
    riskContext,
    activityGroupBucketsByArn,
    resourceIdentities,
  } = useSelector(selectIncidentResponse);
  const { finalSearchTerm } = useSelector(selectSharedState);
  const { onOpenMetadata } = useActiveResourceContext();
  const { getTabIndex } = useMetadataDrawerTabs();
  const { updateActiveResource } = useActiveResourceActions();
  const activityDisclosure = useDisclosure();
  const summaryDisclosure = useDisclosure();
  const [activityResource, setActivityResource] = useState<
    Record<string, any> | undefined
  >();
  const [detailsMapping, setDetailsMapping] = useState<
    Record<string, Record<string, any>>
  >({});
  const [selectedResource, setSelectedResource] = useState<Record<string, any>>(
    {},
  );
  const navigate = useNavigate();
  const { environmentId, accountId } = useAccessBoundary();
  const { accountMapping } = useCloudAccountId();
  const cloudAccountId = accountMapping?.[accountId]?.cloud_account_id;
  const pageNumber = resources.page.info.page_number;
  const pageSize = resources.page.info.page_size;
  const insightCategoriesKey = JSON.stringify(insightCategories);
  const [resourceIdentitiesByArn, setResourceIdentitiesByArn] = useState<
    Record<string, any>
  >({});
  const queryKey = JSON.stringify([
    date,
    environmentId,
    cloudAccountId,
    insightCategoriesKey,
    finalSearchTerm,
  ]);

  const previousQueryKey = useRef(queryKey);
  useEffect(() => {
    if (!date) return;

    const queryChanged = previousQueryKey.current !== queryKey;
    if (queryChanged) {
      previousQueryKey.current = queryKey;
      if (pageNumber !== 1) {
        dispatch(
          actions.updateResourcesPagination({
            page_number: 1,
            page_size: pageSize,
          }),
        );
        return;
      }
    }

    dispatch(
      actions.getCloudEventsResources({
        q: {
          body: {
            account_ids: cloudAccountId ? [cloudAccountId] : [],
            environment_id: environmentId,
            activity_group_id: groupId,
            resource_type: resourceType,
            start_date: dayjs(date).startOf('day').utc().format(),
            end_date: dayjs(date).endOf('day').utc().format(),
            limit: pageSize,
            offset: (pageNumber - 1) * pageSize,
            insight_categories: JSON.parse(insightCategoriesKey),
            search_term: finalSearchTerm || undefined,
          },
        },
        onSuccess: res => {
          if (!isEmpty(res?.results)) {
            const resourceArns = res?.results
              ?.map(row => row.resource_arn)
              ?.filter(Boolean);

            if (resourceArns?.length) {
              dispatch(
                actions.getResourceIdentities({
                  q: {
                    body: {
                      start_date: dayjs(date).startOf('day').utc().format(),
                      end_date: dayjs(date).endOf('day').utc().format(),
                      resource_arns: resourceArns,
                      limit: resourceArns?.length,
                      offset: 0,
                    },
                  },
                  onSuccess: response => {
                    const identitiesByArn: Record<string, any> = {};
                    response?.results?.forEach(bucket => {
                      if (bucket.resource_arn) {
                        identitiesByArn[String(bucket.resource_arn)] = bucket;
                      }
                    });
                    setResourceIdentitiesByArn(identitiesByArn);
                  },
                }),
              );
            }
            dispatch(
              sharedStateActions.getInsightV2All({
                q: {
                  insightV2RequestBase: {
                    resource_type: resourceType,
                    resource_ids: map(res?.results, o => o?.resource_name),
                  },
                },
                onSuccess: details => {
                  if (isEmpty(details?.items)) return;
                  const items: Record<string, Record<string, any>> = {};
                  each(details?.items, insight => {
                    items[insight?.resource_id] = insight;
                  });
                  setDetailsMapping(items);
                  dispatch(
                    actions.getRiskContext({
                      q: {
                        riskContextRequest: {
                          uuids: map(details?.items, o => o?.uuid),
                        },
                      },
                    }),
                  );
                },
              }),
            );
          }
        },
      }),
    );
  }, [
    dispatch,
    resourceType,
    date,
    environmentId,
    cloudAccountId,
    pageNumber,
    pageSize,
    queryKey,
    insightCategoriesKey,
    finalSearchTerm,
    groupId,
  ]);

  useEffect(() => {
    dispatch(
      actions.getActivityGroupBuckets({ q: { activityGroupId: groupId } }),
    );
  }, [groupId, dispatch]);

  const renderIdentityCell = (value: any, identities: any[] = [], label) =>
    !!identities.length ? (
      <CustomTooltip
        label={formatTooltip({
          header: label,
          content: (
            <Stack>
              {identities.map((identity, index) => (
                <HStack align="flex-start">
                  <Box whiteSpace="nowrap">{index + 1}.</Box>
                  <Box key={identity.identity_key ?? identity.identity_name}>
                    {identity.identity_name}
                  </Box>
                </HStack>
              ))}
            </Stack>
          ),
          width: 72,
        })}
      >
        <Box r="25%">{value ?? 0}</Box>
      </CustomTooltip>
    ) : (
      value ?? 0
    );

  const columns: TableColumnProps[] = [
    {
      header: (
        <Box pl={10} ml={1}>
          Resource
        </Box>
      ),
      render: ({ row }) => {
        return (
          <WithResourceIcon
            resourceType={row?.resource_type}
            tooltip={formatTooltip({
              header: 'Resource',
              content: (
                <Stack>
                  {renderTooltipFieldValue('Name', row?.resource_name)}
                  {renderTooltipFieldValue('ARN', row?.resource_arn)}
                  {renderTooltipFieldValue(
                    'Type',
                    getCloudNativeName(row?.resource_type),
                  )}
                </Stack>
              ),
              width: 64,
            })}
          >
            <StackedCell
              upper={row?.resource_name}
              lower={getCloudNativeName(row?.resource_type)}
              showLowerTooltip={false}
              showUpperTooltip={false}
            />
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Folders',
      accessor: 'paths',
      align: 'left',
      styles: {
        cell: { w: '30%' },
        maxWidth: '30%',
      },
      render: ({ row }) => {
        const bucket = activityGroupBucketsByArn[row?.resource_arn];
        const folders = flattenSelectedPaths(bucket?.paths ?? []);
        const remainingFolders = folders.slice(4);
        const remainingFolderCount = folders.length - 4;

        const renderTag = folder => (
          <Tag label={<EllipsisText label={folder.value} />} />
        );

        return folders.length ? (
          <Stack w="full" spacing={1}>
            <HStack w="full" minW={0}>
              {folders?.[0] && renderTag(folders?.[0])}
              {folders?.[1] && renderTag(folders?.[1])}
            </HStack>
            <HStack w="full" minW={0}>
              {folders?.[2] && renderTag(folders?.[2])}
              {folders?.[3] && renderTag(folders?.[3])}
              {remainingFolderCount > 0 && (
                <PlusCountTag
                  count={remainingFolderCount}
                  tooltip={remainingFolders
                    .map(folder => folder.label)
                    .join(', ')}
                />
              )}
            </HStack>
          </Stack>
        ) : (
          renderTag({ value: '/' })
        );
      },
    },
    {
      header: 'Total Requests',
      accessor: 'total_requests',
      render: ({ row }) =>
        renderRequestCount(row?.total_requests, customTheme.colors.blue['300']),
    },
    {
      header: 'Objects Accessed',
      accessor: 'objects_accessed',
      render: ({ row }) => formatNumber(row?.objects_accessed, 1),
    },
    {
      header: 'Data Accessed',
      accessor: 'data_accessed_bytes',
      render: ({ row }) => formatBytes(row?.data_accessed_bytes),
    },
    {
      header: renderTableHeaderWithLoader(
        'Risk Context',
        riskContext.isLoading,
      ),
      render: ({ row }) => {
        return (
          <Center>
            {renderRiskContext(
              riskContext.data?.[detailsMapping?.[row?.resource_name]?.uuid],
              undefined,
              'center',
              6,
            )}
          </Center>
        );
      },
    },

    {
      header: renderTableHeaderWithLoader(
        'Users',
        resourceIdentities.isLoading,
      ),
      accessor: 'users',
      render: ({ row }) =>
        renderIdentityCell(
          row?.users,
          resourceIdentitiesByArn[row?.resource_arn]?.users,
          'Users',
        ),
    },
    {
      header: renderTableHeaderWithLoader(
        'Roles',
        resourceIdentities.isLoading,
      ),
      accessor: 'roles',
      render: ({ row }) =>
        renderIdentityCell(
          row?.roles,
          resourceIdentitiesByArn[row?.resource_arn]?.roles,
          'Roles',
        ),
    },
    {
      header: 'Insights',
      render: renderCloudEventsInsights,
    },
    {
      header: 'Actions',
      render: ({ row }) => (
        <Center onClick={e => e.stopPropagation()}>
          <ActionButton
            icon={<SuddenActivityIcon />}
            label="Show activity"
            onClick={e => {
              e.stopPropagation();
              setActivityResource(row);
              activityDisclosure.onOpen();
            }}
            p={0.5}
            tooltipWidth={20}
          />
          <ActionButton
            icon={<PermissionsActionsIcon />}
            label="Show access"
            onClick={e => {
              e.stopPropagation();
              const insight = detailsMapping[row.resource_name];

              updateActiveResource({
                resourceId: insight?.resource_id ?? row.resource_name,
                resourceType: row.resource_type ?? resourceType,
                uuid: insight?.uuid,
                accountId: insight?.account ?? row.account_id,
                cloudProvider: CloudProvider.Aws,
              });
              onOpenMetadata(
                getTabIndex(row.resource_type, MetadataTabs.Principal),
              );
            }}
            tooltipWidth={20}
          />

          <ActionButton
            icon={<ChartMixedIcon />}
            label="Access summary"
            onClick={e => {
              e.stopPropagation();
              setSelectedResource(row);
              summaryDisclosure.onOpen();
            }}
            p={0.5}
            tooltipWidth={20}
          />
        </Center>
      ),
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
  ];

  return (
    <Box flex={1} w="full" h="full">
      <Table
        columns={columns}
        data={resources.data}
        isLoading={resources.isLoading}
        pagination={{
          pageInfo: resources.page.info,
          onChange: info => dispatch(actions.updateResourcesPagination(info)),
          totalCount: resources.page.totalCount,
        }}
        onRowClick={row =>
          navigate(
            '/visibility/activity/group/' +
              groupId +
              '/resource/' +
              row?.resource_name +
              '?tab=Data&resource_type=' +
              (row.resource_type ?? resourceType) +
              '&groupName=' +
              groupName +
              '&date=' +
              date +
              '&insightCategories=' +
              insightCategories,
          )
        }
      />
      {selectedResource && summaryDisclosure.isOpen && (
        <HourlyAccessSummary
          accountId={selectedResource.account_id}
          date={date}
          isOpen={summaryDisclosure.isOpen}
          onClose={() => {
            summaryDisclosure.onClose();
            setSelectedResource({});
          }}
          resourceArn={selectedResource.resource_arn}
          resourceInsight={detailsMapping[selectedResource?.resource_arn]}
          resourceName={selectedResource.resource_name}
          resourceType={selectedResource.resource_type ?? resourceType}
        />
      )}
      {activityResource && activityDisclosure.isOpen && (
        <CloudEventsActivityDrawer
          activityGroupId={groupId}
          currentResource={activityResource}
          isOpen={activityDisclosure.isOpen}
          onClose={() => {
            activityDisclosure.onClose();
            setActivityResource(undefined);
          }}
          resourceType={activityResource.resource_type ?? resourceType}
          resourceInsight={
            detailsMapping[activityResource.resource_name] ??
            detailsMapping[activityResource.resource_arn]
          }
        />
      )}
    </Box>
  );
};
